import { useCandidateExperienceQuery } from '@/modules/candidate-detail/queries/useCandidateExperienceQuery';
import { useCandidateTouchpointsQuery } from '@/modules/candidate-detail/queries/useCandidateTouchpointsQuery';
import type {
  CandidateDecision,
  CandidateDetailData,
  CandidateQueuePosition,
  CandidateTab,
  PendingDecisionAction,
} from '@/modules/candidate-detail/types';
import {
  getIntroducedLabel,
  getTotalExperience,
} from '@/modules/candidate-detail/utils/candidateProfile';
import type { CandidateStatusApi } from '@/modules/candidates/types';

import { CandidateEvidenceTabs } from './CandidateEvidenceTabs';
import { CandidateFacts } from './CandidateFacts';
import { CandidateHeader } from './CandidateHeader';

interface CandidateDetailProps {
  candidate: CandidateDetailData;
  decision: CandidateDecision;
  jobId: string;
  pendingAction: PendingDecisionAction;
  queue?: CandidateQueuePosition;
  /** Query string to carry through internal links (tenant selection). */
  searchSuffix: string;
  selectedTab: CandidateTab;
  onReject: (rejectionMessage: string | null) => void;
  status: CandidateStatusApi | null;
  onTabChange: (tab: CandidateTab) => void;
}

/**
 * Header, then two flat columns on the paper: facts on the left, evidence
 * tabs on the right. Experience and touchpoints are read here for the
 * current role and the facts; the tabs load them again from the same cache.
 */
export function CandidateDetail({
  candidate,
  decision,
  jobId,
  pendingAction,
  queue,
  searchSuffix,
  selectedTab,
  onReject,
  status,
  onTabChange,
}: CandidateDetailProps) {
  const params = { candidateId: candidate.candidateId, jobId };
  const experienceQuery = useCandidateExperienceQuery(params);
  const touchpointsQuery = useCandidateTouchpointsQuery(params);
  const experience = experienceQuery.data ?? [];
  const currentJob = experience.find((job) => !job.endDate);
  const currentRole = currentJob
    ? [currentJob.jobTitle, currentJob.companyName]
        .map((part) => part.trim())
        .filter(Boolean)
        .join(' · ') || candidate.currentRole
    : candidate.currentRole;
  const totalExperience =
    getTotalExperience(experience) ?? candidate.totalExperience;
  const introducedLabel = getIntroducedLabel(touchpointsQuery.data ?? []);

  const facts = [
    candidate.location && { label: 'Location', value: candidate.location },
    totalExperience && { label: 'Experience', value: totalExperience },
    introducedLabel && { label: 'Introduced', value: introducedLabel },
  ].filter((fact): fact is { label: string; value: string } => Boolean(fact));

  return (
    <div className="flex flex-col gap-8">
      <CandidateHeader
        candidate={candidate}
        currentRole={currentRole}
        decision={decision}
        jobId={jobId}
        onReject={onReject}
        pendingAction={pendingAction}
        queue={queue}
        searchSuffix={searchSuffix}
        status={status}
      />

      <div className="grid gap-10 lg:grid-cols-[240px_minmax(0,1fr)]">
        <CandidateFacts facts={facts} linkedInUrl={candidate.linkedInUrl} />
        <CandidateEvidenceTabs
          candidate={candidate}
          jobId={jobId}
          onTabChange={onTabChange}
          selectedTab={selectedTab}
          status={status}
        />
      </div>
    </div>
  );
}
